import { useEffect, useRef } from "react";
import { useNotify, useTranslate } from "ra-core";
import { useLocation, useNavigate } from "react-router";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { GoogleAuthButton } from "./GoogleAuthButton";

export const LoginPage = () => {
  const { darkModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();
  const hasNotified = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const oauthError = searchParams.get("oauth_error");

    if (!oauthError || hasNotified.current) {
      return;
    }

    hasNotified.current = true;
    notify(oauthError, {
      type: "error",
      messageArgs: { _: oauthError },
    });

    searchParams.delete("oauth_error");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  return (
    <>
      {/* Always dark: the background is the brand, light tokens would wash it out */}
      <div className="dark relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-950 p-6 text-foreground">
        <LoginBackground logo={darkModeLogo} />

        <div className="relative z-10 w-full max-w-sm space-y-8 rounded-2xl border border-white/10 bg-zinc-950/60 p-8 shadow-2xl shadow-indigo-950/50 backdrop-blur-md">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex size-14 items-center justify-center rounded-2xl border bg-card shadow-sm">
              <img className="h-7" src={darkModeLogo} alt="" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          </div>

          <GoogleAuthButton className="w-full">
            {translate("crm.auth.sign_in_google", {
              _: "Continue with Google",
            })}
          </GoogleAuthButton>
        </div>
      </div>
      <Notification />
    </>
  );
};

// Orbit rings centred on the card, each with one glowing point at a fixed angle
const ORBITS = [
  { size: 460, angle: 35 },
  { size: 680, angle: 200 },
  { size: 920, angle: 290 },
  { size: 1180, angle: 120 },
];

const STARS = [
  [8, 22],
  [15, 70],
  [24, 38],
  [33, 88],
  [41, 12],
  [58, 80],
  [66, 28],
  [74, 64],
  [83, 18],
  [90, 46],
  [12, 52],
  [50, 6],
  [95, 84],
  [70, 92],
];

const NOISE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

const LoginBackground = ({ logo }: { logo: string }) => (
  <div aria-hidden className="pointer-events-none absolute inset-0">
    <div
      className="absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"
      style={{
        backgroundImage:
          "linear-gradient(to right, rgb(255 255 255 / 0.06) 1px, transparent 1px), linear-gradient(to bottom, rgb(255 255 255 / 0.06) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    />

    <div className="absolute -left-40 -top-40 size-[560px] rounded-full bg-indigo-500/25 blur-3xl" />
    <div className="absolute -bottom-48 -right-32 size-[620px] rounded-full bg-sky-500/15 blur-3xl" />
    <div className="absolute left-1/2 top-1/2 size-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-600/15 blur-[120px]" />

    {ORBITS.map(({ size, angle }) => (
      <div
        key={size}
        className="absolute left-1/2 top-1/2 rounded-full border border-white/[0.06]"
        style={{
          width: size,
          height: size,
          transform: `translate(-50%, -50%) rotate(${angle}deg)`,
        }}
      >
        <span className="absolute left-1/2 -top-[3px] size-1.5 -translate-x-1/2 rounded-full bg-sky-300 shadow-[0_0_12px_3px_rgb(125_211_252/0.6)]" />
      </div>
    ))}

    {/* Diagonal light streak */}
    <div className="absolute left-1/2 top-1/2 h-px w-[160%] -translate-x-1/2 -translate-y-1/2 -rotate-[24deg] bg-gradient-to-r from-transparent via-sky-300/40 to-transparent" />
    <div className="absolute left-1/2 top-1/2 h-8 w-[160%] -translate-x-1/2 -translate-y-1/2 -rotate-[24deg] bg-gradient-to-r from-transparent via-indigo-400/10 to-transparent blur-xl" />

    {STARS.map(([left, top], i) => (
      <span
        key={i}
        className="absolute size-0.5 rounded-full bg-white/70 animate-pulse"
        style={{
          left: `${left}%`,
          top: `${top}%`,
          animationDelay: `${(i % 5) * 0.6}s`,
          animationDuration: `${3 + (i % 3)}s`,
        }}
      />
    ))}

    <img
      src={logo}
      alt=""
      className="absolute -bottom-32 -right-32 size-[560px] opacity-[0.06]"
    />

    <div
      className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
      style={{ backgroundImage: NOISE }}
    />
  </div>
);
